import { filterCards } from '@/src/utils/srp/searchUtils/filterCards'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '../rootReducer'

// Base selectors
export const selectSelectedSort = (state: RootState) => state.srp.selectedSort
export const selectRecommendedFilter = (state: RootState) => state.srp.recommendedFilter

// Pre-created selectors for onward journey (leg 0)
export const selectOnwardSort = createSelector([selectSelectedSort], selectedSort => selectedSort[0])

export const selectOnwardRecommendedFilter = createSelector(
    [selectRecommendedFilter],
    recommendedFilter => recommendedFilter[0]
)

// Pre-created selectors for return journey (leg 1)
export const selectReturnSort = createSelector([selectSelectedSort], selectedSort => selectedSort[1])

export const selectReturnRecommendedFilter = createSelector(
    [selectRecommendedFilter],
    recommendedFilter => recommendedFilter[1]
)

// Selector factory that takes SRP data and journey leg as parameters
// This creates selectors that can be reused across components
export const createSortedCardIdsSelector = (journeyLeg: 0 | 1) =>
    createSelector(
        [journeyLeg === 0 ? selectOnwardSort : selectReturnSort, (_: RootState, srpData: any) => srpData],
        (selectedSort, data) => {
            const { sortData } = data || {}
            return sortData?.[selectedSort.sortKey]?.[journeyLeg] || []
        }
    )

// Pre-created sorted card IDs selectors
export const selectOnwardSortedCardIds = createSortedCardIdsSelector(0)
export const selectReturnSortedCardIds = createSortedCardIdsSelector(1)

// Selector factory for filtered cards
export const createFilteredCardsSelector = (journeyLeg: 0 | 1) =>
    createSelector(
        [
            journeyLeg === 0 ? selectOnwardSortedCardIds : selectReturnSortedCardIds,
            journeyLeg === 0 ? selectOnwardRecommendedFilter : selectReturnRecommendedFilter,
            (_: RootState, srpData: any) => srpData,
            (_: RootState, srpData: any, shouldShowMultiAirlinePrices: boolean) => shouldShowMultiAirlinePrices,
            (_: RootState, srpData: any, shouldShowMultiAirlinePrices: boolean, srpResultsLoading: boolean) =>
                srpResultsLoading,
            (
                _: RootState,
                srpData: any,
                shouldShowMultiAirlinePrices: boolean,
                srpResultsLoading: boolean,
                isPerPaxPricingEnabled: boolean
            ) => isPerPaxPricingEnabled
        ],
        (sortedCardIds, recommendedFilter, data, showMultiAirlinePrices, isLoading, isPerPaxPricingEnabled) => {
            if (isLoading || !data?.filterData || !sortedCardIds || !data?.cards) {
                return []
            }

            const { cards, filterData, sectors } = data
            const checkedBaggageIncluded = recommendedFilter?.checkedBaggageIncluded || false

            return filterCards({
                selectedLegSortedCardIds: sortedCardIds,
                allCardsForSelectedLeg: cards[journeyLeg] || [],
                filterData,
                sectors,
                checkedBaggageIncludedRecommendedFilter: checkedBaggageIncluded,
                selectedJourneyLeg: journeyLeg,
                shouldShowMultiAirlinePrices: showMultiAirlinePrices,
                isPerPaxPricingEnabled: isPerPaxPricingEnabled
            })
        }
    )

// Pre-created filtered cards selectors
export const selectOnwardFilteredCards = createFilteredCardsSelector(0)
export const selectReturnFilteredCards = createFilteredCardsSelector(1)

// Simple utility selectors
export const selectTotalFlights = (sortedCardIds: string[]) => sortedCardIds?.length || 0
export const selectFlightsAfterFilterCount = (filteredCards: any[]) => filteredCards?.length || 0
export const selectShowEmptyFlightScreen = (filteredCards: any[]) => filteredCards.length === 0
